import Emittery from 'emittery';
import type Result from 'true-myth/result';

import type { SkillShort } from '~/shared/data-contract/skill';

import type { LoadSkillsBySearchTermOptions } from './api/load-skills-by-search-term-request';
import type { ChangeSkill } from './change-skill';
import type { ReportSkill } from './report-skill';
import type { RequestSkill } from './request-skill';

export const fetchSkillsBySearchTermEvent = Symbol('fetch:skills-by-search-term');
export const fetchedSkillsBySearchTermEvent = Symbol('fetched:skills-by-search-term');

export const sendChangeSkillEvent = Symbol('send:change-skill');
export const sentChangeSkillEvent = Symbol('sent:change-skill');

export const sendRequestSkillEvent = Symbol('send:request-skill');
export const sentRequestSkillEvent = Symbol('sent:request-skill');

export const sendReportSkillEvent = Symbol('send:report-skill');
export const sentReportSkillEvent = Symbol('sent:report-skill');

type EmitEventAndPayload = {
    readonly [fetchSkillsBySearchTermEvent]: LoadSkillsBySearchTermOptions;
    readonly [sendChangeSkillEvent]: ChangeSkill;
    readonly [sendReportSkillEvent]: ReportSkill;
    readonly [sendRequestSkillEvent]: RequestSkill;
};

type OnEventAndPayload = {
    readonly [fetchedSkillsBySearchTermEvent]: readonly SkillShort[];
    readonly [sentChangeSkillEvent]: undefined;
    readonly [sentReportSkillEvent]: undefined;
    readonly [sentRequestSkillEvent]: undefined;
};

export type FeedbackEmitteryEvents = EmitEventAndPayload & OnEventAndPayload;

export type FeedbackEmitterDependencies = {
    readonly loadSkillsBySearchTerm: (
        options: LoadSkillsBySearchTermOptions,
        abortSignal?: AbortSignal,
    ) => Promise<Result<readonly SkillShort[], Error>>;
    readonly sendChangeSkill: (change: ChangeSkill) => Promise<Result<unknown, Error>>;
    readonly sendReportSkill: (report: ReportSkill) => Promise<Result<unknown, Error>>;
    readonly sendRequestSkill: (request: RequestSkill) => Promise<Result<unknown, Error>>;
};

export type FeedbackEmitter<TEvents extends EmitEventAndPayload = EmitEventAndPayload> = {
    readonly emit: <TEvent extends keyof EmitEventAndPayload>(event: TEvent, payload: TEvents[TEvent]) => Promise<void>;
    readonly on: Emittery<OnEventAndPayload>['on'];
};

export const createFeedbackEmitter = (dependencies: FeedbackEmitterDependencies): FeedbackEmitter => {
    const { loadSkillsBySearchTerm, sendChangeSkill, sendReportSkill, sendRequestSkill } = dependencies;
    const emitter = new Emittery<FeedbackEmitteryEvents>();
    let skillSearchAbortController = new AbortController();

    const handleFetchSkillsBySearchTermEvent = async (options: LoadSkillsBySearchTermOptions): Promise<void> => {
        skillSearchAbortController.abort();
        skillSearchAbortController = new AbortController();

        const result = await loadSkillsBySearchTerm(options, skillSearchAbortController.signal);

        if (result.isOk) {
            return emitter.emit(fetchedSkillsBySearchTermEvent, result.value);
        }
    };

    const handleRequestSkill = async (requestSkill: RequestSkill): Promise<void> => {
        const requestSkillResponse = await sendRequestSkill(requestSkill);

        if (requestSkillResponse.isOk) {
            return emitter.emit(sentRequestSkillEvent);
        }
    };

    const handleChangeSkill = async (changeSkill: ChangeSkill): Promise<void> => {
        const changeSkillResponse = await sendChangeSkill(changeSkill);

        if (changeSkillResponse.isOk) {
            return emitter.emit(sentChangeSkillEvent);
        }
    };

    const handleReportSkill = async (reportSkill: ReportSkill): Promise<void> => {
        const reportSkillResponse = await sendReportSkill(reportSkill);

        if (reportSkillResponse.isOk) {
            return emitter.emit(sentReportSkillEvent);
        }
    };

    const handlers: { [K in keyof EmitEventAndPayload]: (payload: EmitEventAndPayload[K]) => Promise<void> } = {
        [fetchSkillsBySearchTermEvent]: handleFetchSkillsBySearchTermEvent,
        [sendChangeSkillEvent]: handleChangeSkill,
        [sendReportSkillEvent]: handleReportSkill,
        [sendRequestSkillEvent]: handleRequestSkill,
    };

    const emit: FeedbackEmitter['emit'] = async (event, payload) => {
        return handlers[event](payload);
    };

    return {
        emit,
        on: emitter.on.bind(emitter),
    };
};
